import type { BookingFavoriteDto } from "../../../../../../hooks/Remote/BookingFavorites/booking-favorites.types";
import type {
  BookingInputsFilter,
  BookingType
} from "../../../../../../features/Booking-Form/typings/booking-inputs";
import type { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import type { ISeatStatus } from "../../../../Domain/Types/FloorPlan/SeatStatus.type";
import { isAvailableForFavoriteAutoSelect } from "./SelectFloorPlanView.functions";

export function hasActiveBookingFilters(bookingFilters?: BookingInputsFilter): boolean {
  if (!bookingFilters) {
    return false;
  }
  const { propertyIds, equipmentIds, equipmentCategories, placeCategoryIds, zoneCategoryIds } =
    bookingFilters;
  return (
    (propertyIds?.length ?? 0) > 0 ||
    (equipmentIds?.length ?? 0) > 0 ||
    (equipmentCategories?.length ?? 0) > 0 ||
    (placeCategoryIds?.length ?? 0) > 0 ||
    (zoneCategoryIds?.length ?? 0) > 0
  );
}

/** Skip favorite auto-select only when the floor plan is actively filtered. */
export function shouldSkipFavoriteAutoSelect(filteredSelectables: number[] | undefined): boolean {
  return filteredSelectables != null;
}

export type RecommendedFavoriteAutoSelect =
  | { outcome: "skip" }
  | { outcome: "select_place"; placeId: number; floorInventoryId: number }
  | { outcome: "select_zone"; zoneId: number; floorInventoryId: number };

export type RecommendedFavoriteAutoSelectInput = {
  recommendedFavorite: BookingFavoriteDto | null | undefined;
  bookingType: BookingType | null;
  floorPlan: Pick<IFloorPayload, "floorInventoryId" | "places" | "zones">;
  autoSelectAppliedForFloorId: number | null;
  seatStatus: ISeatStatus;
  skipFavoriteAutoSelect?: boolean;
};

/**
 * Decides whether to auto-select a place or zone from a recommended favorite (once per floor).
 * Skips when the recommendation's `floorInventoryId` does not match the loaded plan (stale query data).
 * Side effects (state, refs) stay in the caller.
 */
export function getRecommendedFavoriteAutoSelect(
  input: RecommendedFavoriteAutoSelectInput
): RecommendedFavoriteAutoSelect {
  const {
    recommendedFavorite,
    bookingType,
    floorPlan,
    autoSelectAppliedForFloorId,
    seatStatus,
    skipFavoriteAutoSelect
  } = input;

  if (recommendedFavorite == null) {
    return { outcome: "skip" };
  }
  if (skipFavoriteAutoSelect) {
    return { outcome: "skip" };
  }
  if (autoSelectAppliedForFloorId === floorPlan.floorInventoryId) {
    return { outcome: "skip" };
  }
  if (recommendedFavorite.bookingType !== bookingType) {
    return { outcome: "skip" };
  }

  const recommendedFloorId = recommendedFavorite.floorInventoryId;
  if (recommendedFloorId != null && recommendedFloorId !== floorPlan.floorInventoryId) {
    return { outcome: "skip" };
  }

  const invId = recommendedFavorite.bookingInventoryId;
  const matchingPlace = floorPlan.places.find(w => (w.inventoryId || w.inventory?.id) === invId);
  if (
    matchingPlace != null &&
    isAvailableForFavoriteAutoSelect(
      { place: matchingPlace, zone: undefined },
      seatStatus,
      bookingType
    )
  ) {
    return {
      outcome: "select_place",
      placeId: matchingPlace.id,
      floorInventoryId: floorPlan.floorInventoryId
    };
  }

  const matchingZone = floorPlan.zones.find(z => z.inventory?.id === invId);
  if (
    matchingZone != null &&
    isAvailableForFavoriteAutoSelect(
      { place: undefined, zone: matchingZone },
      seatStatus,
      bookingType
    )
  ) {
    return {
      outcome: "select_zone",
      zoneId: matchingZone.id,
      floorInventoryId: floorPlan.floorInventoryId
    };
  }

  return { outcome: "skip" };
}

/** Maps auto-select decision to selection state + ref value for the floor effect. */
export type RecommendedFavoriteSelectionPatch =
  | { kind: "none" }
  | {
      kind: "apply";
      selectedPlace: number | undefined;
      selectedZone: number | undefined;
      autoSelectAppliedForFloorId: number;
    };

export function getRecommendedFavoriteSelectionPatch(
  decision: RecommendedFavoriteAutoSelect
): RecommendedFavoriteSelectionPatch {
  if (decision.outcome === "skip") {
    return { kind: "none" };
  }
  if (decision.outcome === "select_place") {
    return {
      kind: "apply",
      selectedPlace: decision.placeId,
      selectedZone: undefined,
      autoSelectAppliedForFloorId: decision.floorInventoryId
    };
  }
  return {
    kind: "apply",
    selectedPlace: undefined,
    selectedZone: decision.zoneId,
    autoSelectAppliedForFloorId: decision.floorInventoryId
  };
}

export type RecommendedFavoriteAutoSelectEffectCallbacks = {
  setSelectedPlace: (id: number | undefined) => void;
  setSelectedZone: (id: number | undefined) => void;
  setAutoSelectAppliedForFloorId: (floorInventoryId: number) => void;
};

/**
 * Runs decision + patch + UI updates for the SelectFloorPlanView recommended-favorite effect.
 */
export function runRecommendedFavoriteAutoSelectEffect(
  input: RecommendedFavoriteAutoSelectInput,
  callbacks: RecommendedFavoriteAutoSelectEffectCallbacks
): void {
  const decision = getRecommendedFavoriteAutoSelect(input);
  const patch = getRecommendedFavoriteSelectionPatch(decision);
  if (patch.kind === "none") {
    return;
  }
  callbacks.setSelectedPlace(patch.selectedPlace);
  callbacks.setSelectedZone(patch.selectedZone);
  callbacks.setAutoSelectAppliedForFloorId(patch.autoSelectAppliedForFloorId);
}
